// src/components/MenuDrawer.js
import React, { useContext, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { apiRequest } from "../services/api";
import { getPublicCategories } from "../services/categories.api";

function MenuDrawer({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const { cartItems } = useCart();
  const [activeTab, setActiveTab] = useState("heels");
  const [productsByCategory, setProductsByCategory] = useState({});
  const [topCategories, setTopCategories] = useState([]);

  const cartCount = cartItems.reduce((sum, i) => sum + i.qty, 0);
  
const [allCategories, setAllCategories] = useState([]);
const [showCategories, setShowCategories] = useState(false);

  useEffect(() => {
  if (!isOpen) return;

  getPublicCategories()
    .then(setAllCategories)
    .catch(() => {});
}, [isOpen]);

  /* ---------------- LOAD PRODUCTS ---------------- */
 useEffect(() => {
  let mounted = true;

  async function load() {
    const res = await getAllProducts();
    const products = res.map(mapBackendProduct);

    // group by category
    const grouped = {};
    products.forEach((p) => {
      if (!grouped[p.category]) grouped[p.category] = [];
      grouped[p.category].push(p);
    });

    // sort categories by count desc
    const sortedCategories = Object.entries(grouped)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 3)
      .map(([key, items]) => ({
        key,
        products: items,
      }));

    if (!mounted) return;

    // set top categories
    setTopCategories(sortedCategories);

    // preload products by category (same structure you already use)
    const data = {};
    sortedCategories.forEach((c) => {
      data[c.key] = c.products;
    });

    setProductsByCategory(data);

    // default active tab = first top category
    setActiveTab(sortedCategories[0]?.key || null);
  }

  if (isOpen) load();
  return () => (mounted = false);
}, [isOpen]);

useEffect(() => {
  if (!isOpen) return;

  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_menu_drawer",
      entity_type: "navigation",
      entity_id: null,
    }),
  }).catch(() => {});
}, [isOpen]);


  const categoryProducts = productsByCategory[activeTab] || [];
  const menuItems = useMemo(() => categoryProducts.slice(0, 3), [categoryProducts]);
  const imageItems = useMemo(() => categoryProducts.slice(0, 3), [categoryProducts]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [isOpen]);

  /* ---------------- NAV HELPERS ---------------- */
  const openCategory = (section) => {
    onClose();
    navigate(`/products?category=${section}`);
  };
  
const openCategoryFromMenu = (slug) => {
  onClose();
  navigate(`/products?category=${slug}`);
};

const openCustomerReviews = () => {
  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_customer_reviews",
      entity_type: "navigation",
      entity_id: null,
    }),
  }).catch(() => {});

  onClose();
  navigate("/customer-reviews");
};

  const openAllProducts = () => {
 apiRequest("/analytics/track", {
   method: "POST",
   body: JSON.stringify({
     action_type: "enter_all_products",
     entity_type: "navigation",
     entity_id: null,
   }),
 }).catch(() => {});

  onClose();
  navigate("/all-products");
};

const openSpecial = (type) => {
  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "enter_special_collection",
      entity_type: "collection",
      entity_id: null, // ✅ FIX: must be INT or null
    }),
  }).catch(() => {});

  onClose();
  navigate(`/products?category=${type}`);
};


  return (
    <div
      className={`fixed inset-0 z-[9999] transition-opacity duration-300
      ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div
        className={`
          absolute top-0 left-0 w-full h-[90vh] bg-white
          transform transition-transform duration-300
          ${isOpen ? "translate-y-0" : "-translate-y-full"}
          md:w-[500px] md:h-screen md:translate-y-0
          md:${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        {/* TOP BAR */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-lightSilver">
          <div className="flex items-center gap-4 px-4 py-2 border border-silver rounded-full bg-white shadow-sm">
            <button
                 onClick={() => {
 apiRequest("/analytics/track", {
   method: "POST",
   body: JSON.stringify({
     action_type: "open_account_from_menu",
     entity_type: "navigation",
     entity_id: null,
   }),
 }).catch(() => {});

  const token = localStorage.getItem("esill-token");
  if (!token) navigate("/create-account");
  else navigate("/account");
}}

               >
              <img src="/icons/user.png" className="w-8 h-8" alt="Account" />
            </button>
            <button onClick={() => navigate("/wishlist")}>
              <img src="/icons/heart.png" className="w-7 h-7" alt="Wishlist" />
            </button>
            <button onClick={() => navigate("/cart")} className="relative">
              <img src="/icons/bag.png" className="w-7 h-7" alt="Cart" />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
          </div>

          <button onClick={onClose} className="p-2">
            <span className="text-3xl text-black">×</span>
          </button>
        </div>

        {/* CATEGORY TABS */}
        <div className="flex justify-center gap-12 mt-4 border-b border-lightSilver px-4 pb-2">
          {topCategories.map((tab) => {
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                onClick={() => {
 apiRequest("/analytics/track", {
   method: "POST",
   body: JSON.stringify({
     action_type: "click_menu_category",
     entity_type: "category",
     entity_id: tab.key,
   }),
 }).catch(() => {});

  setActiveTab(tab.key);
}}
                className={`
                  pb-2 text-base md:text-xl font-semibold
                  ${isActive ? "text-black" : "text-gray-400"}
                  relative
                `}
              >
                <span>
  {lang === "EN"
    ? tab.key.toUpperCase()
    : tab.key === "heels"
    ? "كعب"
    : tab.key === "boots"
    ? "بوتس"
    : tab.key === "sneakers"
    ? "سنيكرز"
    : tab.key}
</span>

                {isActive && (
                  <span className="absolute left-0 right-0 -bottom-[1px] h-[3px] bg-roseNude" />
                )}
              </button>
            );
          })}
        </div>

        {/* CONTENT */}
        <div className="h-[calc(90vh-150px)] overflow-y-auto px-5 pt-5 pb-6">
          {/* TOP LINKS */}
          <div className="flex flex-col space-y-4 mb-4">
            <button
              onClick={() => openSpecial("new-in")}
              className={`text-base md:text-lg font-semibold text-black hover:text-roseNude transition
              ${lang === "AR" ? "text-right" : "text-left"}`}
            >
              {lang === "EN" ? "New Collection" : "مجموعة جديدة"}
              <span className="ml-2 mr-2 text-[10px] md:text-xs px-2 py-[2px] rounded-sm bg-roseNude text-black font-bold">
                {lang === "EN" ? "NEW" : "جديد"}
              </span>
            </button>

{/* CATEGORIES DROPDOWN */}
<div className={`${lang === "AR" ? "text-right" : "text-left"}`}>
  <button
    onClick={() => setShowCategories((p) => !p)}
    className="mt-2 text-base md:text-lg font-semibold text-black hover:text-roseNude transition flex items-center gap-2"
  >
    {lang === "EN" ? "Shoes" : "أحذية"}
    <span className="text-sm">{showCategories ? "▲" : "▼"}</span>
  </button>

  {showCategories && (
    <div className="mt-2 ml-4 space-y-2">
      {allCategories.map((c) => (
        <button
          key={c.slug}
          onClick={() => openCategoryFromMenu(c.slug)}
          className="block text-sm text-gray-700 hover:text-roseNude transition"
        >
          {lang === "EN" ? c.name_en : c.name_ar}
        </button>
      ))}
    </div>
  )}
</div>

<button
              onClick={() => openSpecial("bags")}
              className={`text-base md:text-lg font-semibold text-black hover:text-roseNude transition
              ${lang === "AR" ? "text-right" : "text-left"}`}
            >
              {lang === "EN" ? "Bags" : "حقائب"}
            </button>

<button
              onClick={() => openSpecial("sale")}
              className={`text-base md:text-lg font-semibold text-black hover:text-roseNude transition
              ${lang === "AR" ? "text-right" : "text-left"}`}
            >
              {lang === "EN" ? "Sale" : "تخفيضات"}
            </button>

<button
               onClick={openAllProducts}
               className={`text-base md:text-lg font-semibold text-black hover:text-roseNude transition
               ${lang === "AR" ? "text-right" : "text-left"}`}
             >
               {lang === "EN" ? "Shop All" : "تصفح كل المنتجات"}

               <span className="ml-2 mr-2 text-[10px] md:text-xs px-2 py-[2px] rounded-sm bg-roseNude text-black font-bold">
                 {lang === "EN" ? "ALL" : "الكل"}
               </span>
             </button>

<button
  onClick={openCustomerReviews}
  className={`text-base md:text-lg font-semibold text-black hover:text-roseNude transition
  ${lang === "AR" ? "text-right" : "text-left"}`}
>
  {lang === "EN" ? "Reviews" : "مراجعات العملاء"}

  <span className="ml-2 mr-2 text-[10px] md:text-xs px-2 py-[2px] rounded-sm bg-roseNude text-black font-bold">
    {lang === "EN" ? "REVIEWS" : "آراء"}
  </span>
</button>

          </div>

          {/* PRODUCT TITLES */}
          <div className="space-y-4 mb-10">
            {menuItems.map((p) => (
              <button
                key={p.id}
                onClick={() => openCategory(activeTab)}
                className="w-full flex items-center justify-between text-base md:text-lg text-black hover:text-roseNude transition"
              >
                <span>{lang === "EN" ? p.nameEN : p.nameAR}</span>
                <span className="text-lg md:text-xl text-gray-400">
                  {lang === "EN" ? "›" : "‹"}
                </span>
              </button>
            ))}
          </div>

          {/* IMAGES */}
          <div className="grid grid-cols-3 gap-3">
            {imageItems.map((p) => (
              <button key={p.id} onClick={() => openCategory(activeTab)} className="relative overflow-hidden bg-black group">
                <img
                  src={p.images[0] || "/images/placeholder-product.jpg"}
                  className="w-full h-40 md:h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                  alt={p.nameEN}
                />
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-40 transition" />
                <span className="absolute bottom-2 left-2 right-2 text-[10px] md:text-sm font-semibold text-white drop-shadow-md">
                  {lang === "EN" ? p.nameEN : p.nameAR}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MenuDrawer;
